import { Tab, Tabs, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../../core';
import { HBox } from '../../core/layout';
import { RequestActivity, Spinner } from '../../core/loading';
import { PageDrawer } from '../../core/page';
import { lightGrayBorder, mediumGray } from '../../core/theme';
import { toggleButtonApi } from '../../core/toggle-button';
import {
  VisitContext,
  hospitalistEnabled,
  navigateToList,
  visitContextApi,
} from '../../core/visit-context';
import { worksheetQueue } from '../../core/visit-queue';
import { isReviewWorkflow, workflows } from '../../core/workflow-api';
import { Summary } from '../common/audit-summary';
import {
  CodeReference,
  CodingListLink,
  DocumentsLink,
  LogoutLink,
  ReportBug,
} from '../common/navigation';
import { AddNoteDialog } from '../common/notes';
import { TabPanel } from '../common/tabs';
import { CodingActionsDialogue } from './coding-actions';
import { VisitDrawer, visitDrawerTabs } from './coding-components';
import { HospitalistCodingRoute } from './hospitalist-coding-route';
import { WorksheetSelectionMenu } from './related-worksheets';
import { WorksheetTab } from './worksheet';
import { NoteSummary } from './note-summary';

function CodingTabs(props?: VisitContext) {
  const {
    worksheets,
    worksheetTypes,
    hasAuditSummary,
    notesApi,
    hospitalist,
    hospitalistContext,
    visit,
    workflowNames,
  } = props;
  const [value, setValue] = useState(props.readonly ? 1 : 0);

  const onChange = (_: any, i: number) => {
    setValue(i);
  };

  workflows.data((s) => s.feature);

  const w = workflows.data((s) => s.workflow);
  const flow = w?.prompt || w?.name || '';

  //TODO handle non pro-fee
  const transition = worksheets[0].transition;

  const [transitionOpen, setTransitionOpen] = useState(false);
  const onVisitClick = () => {
    transition.setState({
      openTransitionDialogue: true,
      transitionDialogueComplete: () => {
   
        navigateToList(visit);
      },
    });
    setTransitionOpen(true);
  };


  const auditReviewEnabled = hasAuditSummary || isReviewWorkflow();

  notesApi.apis = worksheets;
  //console.info('auditReviewEnabled', worksheets);

  useEffect(() => {
    if (props.readonly) {
      setTimeout(() => setValue(0), 20);
    }
  }, []);

  console.info('This is a worksheet config');
  console.info(worksheets[0].downCode.form.values.getState().value);
  return (
    <>
      <RequestActivity />
      <div style={{ width: '100%', maxWidth: '100vw' }}>
        <Tabs
          style={{ paddingLeft: '56px', paddingTop: '0px', marginTop: 0 }}
          value={value}
          onChange={onChange}
          id="coding-tabs"
        >
          {hospitalist
            ? worksheetTypes.map((v, i) => (
                <Tab
                  key={v}
                  id={"coding-tab-" + v}
                  label={
                    <WorksheetSelectionMenu
                      hospitalist={hospitalistContext}
                      api={worksheets[i].context.selection}
                      visit={visit}
                      worksheet={worksheets[i].context.worksheet}
                    />

                  }
                />
              ))
            : worksheetTypes.map((v, i) => (
                <Tab
                  key={v}
                  id={"coding-tab-" + v}
                  label={
                    v + ' - ' + worksheets[i].context.worksheet.departmentCode
                  }
                />
              ))}

          {auditReviewEnabled && <Tab label="Audit Summary "></Tab>}
        </Tabs>
        {worksheetTypes.map((w, i) => (
          <TabPanel key={w} value={value} index={i}>
            <WorksheetTab {...worksheets[i]} />
          </TabPanel>
        ))}

        {auditReviewEnabled && (
          <TabPanel value={value} index={worksheets.length}>
            <Summary apis={notesApi.apis} workflows={workflowNames} />
          </TabPanel>
        )}
      </div>
      <div
        style={{ position: 'absolute', right: '12px', top: '0px', zIndex: 10 }}
      >
        <HBox spacing={0}>
          <Typography variant="h6" style={{ color: mediumGray }}>
            {`${flow} Workflow ${props.readonly ? ' (Read Only)' : ''}`}
          </Typography>

          <DocumentsLink htmlColor={mediumGray} />
          <CodeReference htmlColor={mediumGray} api={worksheets[0].icdCpt} />
          {!props.readonly && (
            <CodingListLink onClick={onVisitClick} htmlColor={mediumGray} />
          )}
          <ReportBug htmlColor={mediumGray} />
          <LogoutLink
            htmlColor={mediumGray}
            onClick={() => auth.signOut(true)}
          />
        </HBox>
      </div>

      <AddNoteDialog {...notesApi} />

      <CodingActionsDialogue
        label="Select a Worksheet Action Before Continuing"
        transition={transition}
        actions={transition.getState().actions}
      />
    </>
  );
}

const toggleSideBar = toggleButtonApi();
export const codingDrawerWidth = 362;

export function CodingTool() {
  const ctx = visitContextApi((s) => s.context);
  useEffect(() => {
    visitDrawerTabs.setState({ index: 0 });
  }, []);
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: '0px',
          top: '0px',
          width: '100%',
          height: '48px',
          zIndex: -1,
          borderBottom: `solid 1px ${lightGrayBorder}`,
          backgroundColor: 'white',
        }}
      />
      <PageDrawer
        main={CodingTabs(ctx)}
        drawerHeader={'Visit - ' + ctx.visit.locationCode}
        drawerWidth={codingDrawerWidth}
        drawerOpen={true}
        drawer={VisitDrawer(ctx)}
        toggle={toggleSideBar}
      />
    </>
  );
}

export function VisitContextLoader({ children }) {
  const ctx = visitContextApi((s) => s.context);
  const loading = visitContextApi((s) => s.loading);
  toggleSideBar.setState({ active: true });
  return <Spinner complete={!loading && ctx}>{children}</Spinner>;
}

export function NotesWrapper({ ctx }: { ctx: VisitContext }) {
  const notes = ctx.notesApi.useNotes((s) => s.notes);
  const noteContexts = Object.entries(notes ?? {});

  const [showSummary, setShowSummary] = useState(true);

  const totalNotes = noteContexts.reduce(
    (sum, [, notesArray]) => sum + notesArray.length,
    0
  );

  useEffect(() => {
    if (totalNotes === 0) {
      setShowSummary(false);
    }
  }, [totalNotes]);

  return showSummary ? (
    <NoteSummary api={ctx.notesApi} hospitalistContext={ctx.hospitalistContext} />
  ) : null;
}


export function CodingRoute() {
  if (!auth.isAuthenticated()) {
    return <Navigate replace to="/" />;
  }
  const visit = worksheetQueue((s) => s.visit);
  if (!visit) {
    return <Navigate replace to="/list" />;
  }
  visitDrawerTabs.setState({ index: 0 });
  const ctx = visitContextApi((s) => s.context);

  const isHospitalist = hospitalistEnabled(visit, !!visit.wsSummaries.find(a => a.departmentCode === 'HOSPITALIST') );
  
  return isHospitalist ? (
    <HospitalistCodingRoute ctx={ctx}/>
  ) : (
    <VisitContextLoader>
      {ctx && <NotesWrapper ctx={ctx} />}
      <CodingTool />
    </VisitContextLoader>
  );
}
